import { forwardRef, useCallback, useRef } from 'react';
import type { ChangeEvent, PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';

import {
  useConsolidatedRef,
  useElement,
  useFocusWithin,
  useI18n,
  useInputFormatter,
  useTestIds
} from '../../hooks';
import { defaultThemeProp } from '../../theme';
import type {
  BaseProps,
  ForwardRefForwardPropsComponent,
  NoChildrenProp,
  PropsWithDefaults,
  TestIdProp
} from '../../types';
import FormField from '../FormField';
import { StyledFormControl } from '../FormControl';
import type { FormControlProps } from '../FormControl';
import StyledInput from '../Input/Input.styles';
import Select, { Option } from '../Select';
import Input from '../Input';
import { StyledSelect } from '../Select/Select';
import Flex from '../Flex';
import { withTestIds, createClassName } from '../../utils';
import Tooltip from '../Tooltip';

import callingCodes from './callingCodes';
import {
  convertToCallbackParameter,
  formatPhone,
  formatPhoneNumber,
  getPhoneNumberParts
} from './utils';
import { getPhoneInputTestIds } from './Phone.test-ids';

type AutoFillTel =
  | AutoFillBase
  | `${OptionalPrefixToken<AutoFillSection>}${OptionalPrefixToken<AutoFillAddressKind>}${OptionalPrefixToken<AutoFillContactKind>}tel`;

export interface PhoneInputProps extends FormControlProps, BaseProps, NoChildrenProp, TestIdProp {
  /**
   * Display select with countries calling codes
   * @default false
   */
  showCountryCode?: boolean;
  /** Changes lists of countries calling codes */
  callingCodesList?: string[];
  /** Callback fired on input value change */
  onChange?: (value: string) => void;
  /** Callback fired when the component loses focus */
  onBlur?: (value: string) => void;
  /** CCallback fired when the component gets focus */
  onFocus?: (value: string) => void;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLInputElement>;
  /** Flag defining autocompletion behavior. */
  autoComplete?: AutoFillTel;
}

type PhoneInputPropsWithDefaults = PropsWithDefaults<PhoneInputProps>;

const StyledPhoneInput = styled.div<PhoneInputPropsWithDefaults>(() => {
  return css`
    border: 0;

    > ${StyledSelect} {
      max-width: max-content;
      border-inline-end: none;
      border-start-end-radius: 0;
      border-end-end-radius: 0;
    }

    > ${StyledSelect} + ${StyledInput} {
      border-start-start-radius: 0;
      border-end-start-radius: 0;
    }

    ${StyledSelect},
    ${StyledInput} {
      &:focus {
        z-index: 1;
      }
    }
  `;
});

StyledPhoneInput.defaultProps = defaultThemeProp;

const PhoneInput: ForwardRefForwardPropsComponent<PhoneInputProps> = forwardRef(function PhoneInput(
  props: PropsWithoutRef<PhoneInputProps>,
  ref: PhoneInputProps['ref']
) {
  const {
    testId,
    value,
    id,
    label,
    labelHidden,
    info,
    status,
    required,
    readOnly,
    disabled,
    showCountryCode = false,
    callingCodesList = callingCodes,
    onChange,
    onBlur,
    onFocus,
    onResolveSuggestion,
    additionalInfo,
    className,
    autoComplete = '_off',
    ...restProps
  } = props;

  const testIds = useTestIds(testId, getPhoneInputTestIds);
  const [selectEl, setSelectEl] = useElement<HTMLSelectElement>();
  const [inputEl, setInputEl] = useElement<HTMLInputElement>();

  const t = useI18n();

  const [countryCode = callingCodesList[0], subscriberNumber = ''] = value
    ? getPhoneNumberParts(value, callingCodesList)
    : [];

  const onFocusChange = (focused: boolean) => {
    const callbackParam = convertToCallbackParameter(countryCode, subscriberNumber);
    if (focused) onFocus?.(callbackParam);
    else onBlur?.(callbackParam);
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange?.(convertToCallbackParameter(countryCode, e.target.value));
  };

  const onSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onChange?.(convertToCallbackParameter(e.target.value, subscriberNumber));
  };

  const inputRef = useConsolidatedRef<HTMLInputElement>(ref, setInputEl);
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusWithin([containerRef], onFocusChange);

  const formatter = useCallback(
    (number: string) => {
      if (value && readOnly && showCountryCode) {
        const temp = formatPhone(value, { nationalFormat: !readOnly });
        if (temp) return temp;
      }
      return formatPhoneNumber(number.replace(/\D+/g, ''), countryCode, showCountryCode);
    },
    [readOnly, countryCode, showCountryCode]
  );

  const formatted = useInputFormatter(inputRef, subscriberNumber, formatter);

  const codesOptions = (
    <>
      {callingCodesList.map(code => (
        <Option key={code} value={code}>
          {code}
        </Option>
      ))}
    </>
  );

  const Comp = (
    <StyledPhoneInput
      data-testid={testIds.root}
      as={StyledFormControl}
      forwardedAs={Flex}
      container={{ alignItems: 'center', wrap: 'nowrap' }}
      ref={containerRef}
      status={status}
      readOnly={readOnly}
      disabled={disabled}
    >
      <>
        {showCountryCode && !readOnly && (
          <>
            <Select
              data-testid={testIds.countryCode}
              value={countryCode}
              aria-label={t('select_country_code')}
              onChange={onSelectChange}
              required={required}
              status={status}
              readOnly={readOnly}
              disabled={disabled}
              autoComplete={
                autoComplete && autoComplete.endsWith('tel')
                  ? (autoComplete.replace('tel', 'tel-country-code') as AutoFillTel)
                  : autoComplete
              }
              ref={setSelectEl}
              hasSuggestion={status === 'pending' && !!onResolveSuggestion}
            >
              {codesOptions}
            </Select>
            {selectEl && (
              <Tooltip target={selectEl} describeTarget={false} showDelay='none' hideDelay='none'>
                {t('country_code')}
              </Tooltip>
            )}
          </>
        )}
        <Input
          data-testid={testIds.control}
          autoComplete={
            autoComplete && autoComplete.endsWith('tel')
              ? (autoComplete.replace('tel', 'tel-national') as AutoFillTel)
              : autoComplete
          }
          {...restProps}
          inputMode='tel'
          ref={inputRef}
          value={formatted}
          aria-label={t('enter_a_number')}
          onChange={onInputChange}
          required={required}
          status={status}
          readOnly={readOnly}
          disabled={disabled}
          onResolveSuggestion={onResolveSuggestion}
          className={createClassName('phone-input', className)}
        />
        {showCountryCode && inputEl && (
          <Tooltip target={inputEl} describeTarget={false} showDelay='none' hideDelay='none'>
            {t('phone_number')}
          </Tooltip>
        )}
      </>
    </StyledPhoneInput>
  );

  return label ? (
    <FormField
      testId={testIds}
      as='fieldset'
      additionalInfo={additionalInfo}
      label={label}
      labelHidden={labelHidden}
      id={id}
      info={info}
      readOnly={readOnly}
      status={status}
      required={required}
      disabled={disabled}
      onResolveSuggestion={onResolveSuggestion}
    >
      {Comp}
    </FormField>
  ) : (
    Comp
  );
});

export default withTestIds(PhoneInput, getPhoneInputTestIds);
